import React, { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { ArrowLeft, FileText, Upload, Download, Copy } from "lucide-react";
import { motion } from "framer-motion";

export default function ContasaPagarZendry() {
  const [csvData, setCsvData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [ofxData, setOfxData] = useState(null);
  const [contabilData, setContabilData] = useState(null);
  const [conciliacaoResultado, setConciliacaoResultado] = useState(null);
  const fileInputRef = useRef(null);
  const ofxInputRef = useRef(null);
  const contabilInputRef = useRef(null);

  // Função para processar o valor monetário
  const parseValue = (value) => {
    if (!value || value === '---') return 0;
    // Remove espaços e converte vírgula para ponto
    const cleanValue = value.toString().replace(/\s/g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  const parseValorBR = (value) => {
    if (!value || value === '---') return 0;
    const cleanValue = value.toString().replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Função para processar o CSV
  const processCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const headers = lines[0].split(';');
    const data = [];
    
    let totalCredito = 0;
    let totalDebito = 0;
    let saldoFinal = 0;

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length >= 8) {
        const row = {
          descricao: values[0],
          referencia1: values[1],
          referencia2: values[2],
          referencia3: values[3],
          creditado: values[4],
          debitado: values[5],
          data: values[6],
          valor: parseValue(values[7]),
          saldo: parseValue(values[8])
        };
        
        data.push(row);
        
        // Log das primeiras 5 transações para debug
        if (i <= 5) {
          console.log(`📝 Transação ${i} (CSV):`, {
            descricao: row.descricao,
            valor: row.valor,
            valorOriginal: values[7],
            saldo: row.saldo,
            saldoOriginal: values[8],
            creditado: row.creditado,
            debitado: row.debitado
          });
        }
        
        // Calcular totais
        if (row.valor > 0) {
          totalCredito += row.valor;
        } else {
          totalDebito += Math.abs(row.valor);
        }
        
        // O saldo final é o último saldo do arquivo
        saldoFinal = row.saldo;
      }
    }

    return {
      data,
      summary: {
        totalCredito,
        totalDebito,
        saldoFinal,
        totalTransacoes: data.length
      }
    };
  };

  // Função para lidar com o upload do arquivo
  const handleFileUpload = (file) => {
    if (!file) return;
    
    setIsProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = processCSV(csvText);
        setCsvData(result.data);
        setSummary(result.summary);
      } catch (error) {
        console.error('Erro ao processar CSV:', error);
        alert('Erro ao processar o arquivo CSV. Verifique o formato do arquivo.');
      } finally {
        setIsProcessing(false);
      }
    };
    
    reader.readAsText(file);
  };

  // Eventos de drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Função para formatar valores monetários
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para extrair ID da transação da descrição
  const extrairIdTransacao = (descricao) => {
    const match = descricao.match(/\d+/);
    return match ? match[0] : Math.random().toString(36).substr(2, 9);
  };

  const normalizarDataParaChave = (data) => {
    if (!data) return '';
    const matchBr = data.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (matchBr) {
      return `${matchBr[3]}${matchBr[2]}${matchBr[1]}`;
    }
    const matchOfx = data.match(/(\d{4})(\d{2})(\d{2})/);
    if (matchOfx) {
      return `${matchOfx[1]}${matchOfx[2]}${matchOfx[3]}`;
    }
    return data;
  };

  const copiarListaDivergencias = (itens, tipo) => {
    const texto = itens
      .map((item) => {
        const valor = (item.valorAbs ?? item.valor ?? 0).toFixed(2);
        const descricao = tipo === 'ofx' ? item.memo : item.historico;
        return `${item.data};${valor};${descricao || ''}`;
      })
      .join('\n');

    navigator.clipboard.writeText(texto)
      .catch(() => undefined);
  };

  const copiarDivergencias = (tipo) => {
    if (!conciliacaoResultado) return;
    const itens = tipo === 'ofx'
      ? conciliacaoResultado.somenteOFX
      : conciliacaoResultado.somenteContabil;
    copiarListaDivergencias(itens, tipo);
  };

  const processarOFX = (conteudo) => {
    const transacoes = [];
    const regex = /<STMTTRN>([\s\S]*?)<\/STMTTRN>/g;
    let match;

    while ((match = regex.exec(conteudo)) !== null) {
      const transacao = match[1];
      const valorMatch = transacao.match(/<TRNAMT>([-\d.]+)/);
      const dataMatch = transacao.match(/<DTPOSTED>(\d{8})/);
      const tipoMatch = transacao.match(/<TRNTYPE>(\w+)</);
      const memoMatch = transacao.match(/<MEMO>([^\r\n<]*)/);

      if (valorMatch && dataMatch) {
        const valorBruto = parseFloat(valorMatch[1]);
        const tipo = tipoMatch ? tipoMatch[1] : '';
        const memo = memoMatch ? memoMatch[1].trim() : '';
        const dataStr = dataMatch[1];
        const dataFormatada = `${dataStr.substring(6, 8)}/${dataStr.substring(4, 6)}/${dataStr.substring(0, 4)}`;
        const valor = tipo === 'DEBIT' && valorBruto > 0 ? -valorBruto : valorBruto;
        const valorAbs = Math.abs(valorBruto);
        const idTransacao = extrairIdTransacao(memo);

        transacoes.push({
          data: dataFormatada,
          dataKey: normalizarDataParaChave(dataFormatada),
          tipo,
          memo,
          idTransacao,
          valor,
          valorAbs
        });
      }
    }

    const totalValor = transacoes.reduce((sum, item) => sum + item.valorAbs, 0);
    setOfxData({ transacoes, totalValor });
    return { transacoes, totalValor };
  };

  const processarCSVContabil = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(';');
      if (values.length >= 6) {
        const dataMovimento = values[0]?.trim();
        const debito = values[2]?.trim();
        const credito = values[3]?.trim();
        const valor = parseValorBR(values[4]);
        const historico = values[5] || '';
        const idTransacao = extrairIdTransacao(historico);

        data.push({
          data: dataMovimento,
          dataKey: normalizarDataParaChave(dataMovimento),
          debito,
          credito,
          valor,
          valorAbs: Math.abs(valor),
          historico,
          idTransacao
        });
      }
    }

    const totalValor = data.reduce((sum, item) => sum + item.valorAbs, 0);
    setContabilData({ transacoes: data, totalValor });
    return { transacoes: data, totalValor };
  };

  const realizarConciliacao = () => {
    if (!ofxData || !contabilData) {
      alert('Faça upload do OFX e do CSV contábil para conciliar.');
      return;
    }

    const contabMapPorId = new Map();
    const contabMapPorValor = new Map();
    contabilData.transacoes.forEach((item, index) => {
      if (item.idTransacao && item.idTransacao !== 'N/A') {
        const chave = `${item.dataKey}|${item.valorAbs}|${item.idTransacao}`;
        const lista = contabMapPorId.get(chave) || [];
        lista.push(index);
        contabMapPorId.set(chave, lista);
      }
      const chaveValor = `${item.dataKey}|${item.valorAbs}`;
      const listaValor = contabMapPorValor.get(chaveValor) || [];
      listaValor.push(index);
      contabMapPorValor.set(chaveValor, listaValor);
    });

    const usadosContabil = new Set();
    const correspondencias = [];
    const somenteOFX = [];

    ofxData.transacoes.forEach((item) => {
      let indiceMatch = null;
      if (item.idTransacao && item.idTransacao !== 'N/A') {
        const chave = `${item.dataKey}|${item.valorAbs}|${item.idTransacao}`;
        const lista = contabMapPorId.get(chave) || [];
        while (lista.length) {
          const indice = lista.shift();
          if (!usadosContabil.has(indice)) {
            indiceMatch = indice;
            break;
          }
        }
      }

      if (indiceMatch === null) {
        const chaveValor = `${item.dataKey}|${item.valorAbs}`;
        const listaValor = contabMapPorValor.get(chaveValor) || [];
        while (listaValor.length) {
          const indice = listaValor.shift();
          if (!usadosContabil.has(indice)) {
            indiceMatch = indice;
            break;
          }
        }
      }

      if (indiceMatch !== null) {
        usadosContabil.add(indiceMatch);
        correspondencias.push({
          ofx: item,
          contabil: contabilData.transacoes[indiceMatch]
        });
      } else {
        somenteOFX.push(item);
      }
    });

    const somenteContabil = contabilData.transacoes.filter((_, index) => !usadosContabil.has(index));
    const diferencaTotal = Math.abs(contabilData.totalValor - ofxData.totalValor);
    const taxaConciliacao = (correspondencias.length / Math.max(ofxData.transacoes.length, contabilData.transacoes.length)) * 100;

    setConciliacaoResultado({
      totalOFX: ofxData.totalValor,
      totalContabil: contabilData.totalValor,
      diferencaTotal,
      correspondencias,
      somenteOFX,
      somenteContabil,
      taxaConciliacao
    });
  };

  const handleOFXUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        processarOFX(e.target.result);
      } catch (error) {
        console.error('Erro ao processar OFX:', error);
        alert('Erro ao processar o arquivo OFX. Verifique o formato do arquivo.');
      }
    };
    reader.readAsText(file);
  };

  const handleContabilUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        processarCSVContabil(e.target.result);
      } catch (error) {
        console.error('Erro ao processar CSV contábil:', error);
        alert('Erro ao processar o arquivo CSV contábil. Verifique o formato do arquivo.');
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  // Função para formatar data no formato DDMMAAAA
  const formatarDataCSV = (dataOriginal) => {
    // Extrair apenas a parte da data (antes do espaço, se houver)
    const apenasData = dataOriginal.split(' ')[0];
    const [dia, mes, ano] = apenasData.split('/');
    return `${dia.padStart(2, '0')}${mes.padStart(2, '0')}${ano}`;
  };

  // Função para extrair competência do CSV (MMAAAA)
  const extrairCompetencia = () => {
    if (!csvData.length) {
      return '012024'; // Fallback
    }
    
    // Pegar a primeira transação para extrair mês/ano
    const primeiraData = csvData[0].data;
    const match = primeiraData.match(/\d{2}\/(\d{2})\/(\d{4})/);
    
    if (match) {
      const mes = match[1];
      const ano = match[2];
      return `${mes}${ano}`;
    }
    
    return '012024'; // Fallback
  };

  // Função para remover acentos e converter para maiúsculo
  const removerAcentosEMaiusculo = (texto) => {
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  };

  // Função para gerar CSV formatado com lógica específica para contas a pagar
  const gerarCSVFormatado = () => {
    if (!csvData || csvData.length === 0) {
      alert('Nenhum dado disponível para gerar CSV');
      return;
    }

    console.log('🔄 Iniciando geração do CSV para Contas a Pagar...');
    
    // Filtrar apenas transações de crédito (valores positivos)
    const transacoesCredito = csvData.filter(row => row.valor > 0);
    
    console.log(`📊 Total de transações de crédito encontradas: ${transacoesCredito.length}`);
    
    const linhasCSV = [];
    
    // Não adicionar cabeçalho - seguir formato da ContaReceitaZendry
    
    transacoesCredito.forEach((transacao, index) => {
      const dataFormatada = formatarDataCSV(transacao.data);
      const valor = transacao.valor.toFixed(2); // Manter formato float com ponto decimal
      const zero = 0;
      const idTransacao = extrairIdTransacao(transacao.descricao);
      
      // Lógica específica para contas a pagar:
      let codigoContaDebito = 18;
      let codigoContaCredito;
      
      // Verificar se é ESTORNO ou Devolução (código especial 2885)
      // PRIORIDADE: Verificar primeiro se a descrição inicia com ESTORNO ou Devolução
      const isEstornoOuDevolucao = transacao.descricao.startsWith('ESTORNO') || transacao.descricao.startsWith('Devolução');
      
      if (isEstornoOuDevolucao) {
        // Para ESTORNO e Devolução: código 2885 (independente dos envolvidos)
        codigoContaCredito = 2885;
      } else if (transacao.debitado === 'ZENDRY S.A') {
        // Se o cliente (debitado) for "ZENDRY S.A": débito 18, crédito 16
        codigoContaCredito = 16;
      } else {
        // Se o cliente (debitado) não for "ZENDRY S.A": débito 18, crédito 2893
        codigoContaCredito = 2893;
      }
      
      const clienteOriginal = transacao.debitado || 'N/A';
      const clienteDebitado = clienteOriginal.replace(/,/g, ''); // Remover vírgulas do nome
      const descricaoSemAcento = removerAcentosEMaiusculo(`Transacao #${idTransacao} Cliente ${clienteDebitado}`);
      const descricao = `"${descricaoSemAcento}"`;

      // Formato: Data,ContaDebito,ContaCredito,Valor,Zero,Descrição (igual ContaReceitaZendry)
      const linha = `${dataFormatada},${codigoContaDebito},${codigoContaCredito},${valor},${zero},${descricao}`;
      linhasCSV.push(linha);
      
      // Log das primeiras 3 transações para debug
      if (index < 3) {
        console.log(`📝 Linha ${index + 1} CSV:`, {
          descricao: transacao.descricao,
          creditado: transacao.creditado,
          debitado: transacao.debitado,
          isEstornoOuDevolucao: isEstornoOuDevolucao,
          codigoDebito: codigoContaDebito,
          codigoCredito: codigoContaCredito,
          valor: valor,
          linha: linha
        });
      }
      
      // Log especial para ESTORNO e Devolução
      if (isEstornoOuDevolucao) {
        console.log(`🔄 ESTORNO/DEVOLUÇÃO detectado:`, {
          descricao: transacao.descricao,
          creditado: transacao.creditado,
          debitado: transacao.debitado,
          codigoCredito: codigoContaCredito
        });
      }
    });
    
    // Criar e baixar o arquivo CSV
    const csvContent = linhasCSV.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    a.download = `CONTAS A PAGAR LANÇAMENTOS CSV QUESTOR ${extrairCompetencia()}.csv`;
    
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    console.log(`✅ CSV gerado com sucesso! ${transacoesCredito.length} transações processadas.`);
  };

  // Função para baixar JSON dos dados
  const downloadJSON = () => {
    if (!csvData || csvData.length === 0) {
      alert('Nenhum dado disponível para download');
      return;
    }

    const dataToExport = {
      summary,
      transacoes: csvData,
      geradoEm: new Date().toISOString(),
      competencia: extrairCompetencia()
    };

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `DADOS CONTA A PAGAR JSON QUESTOR ${extrairCompetencia()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Função para copiar JSON
  const copyJSON = () => {
    if (!csvData || csvData.length === 0) {
      alert('Nenhum dado disponível para copiar');
      return;
    }

    const dataToExport = { summary, transacoes: csvData };
    navigator.clipboard.writeText(JSON.stringify(dataToExport, null, 2))
      .then(() => alert('Dados copiados para a área de transferência!'))
      .catch(() => alert('Erro ao copiar dados'));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Link
                to={createPageUrl("Operations")}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Link>
              <div className="flex items-center space-x-2">
                <FileText className="w-6 h-6 text-orange-600" />
                <h1 className="text-xl font-semibold text-gray-900">
                  Contas a Pagar - Zendry
                </h1>
              </div>
            </div>
            <nav className="text-sm text-gray-500">
              <span>Operações</span>
              <span className="mx-2">/</span>
              <span>Contábil Questor</span>
              <span className="mx-2">/</span>
              <span>Zendry S.A</span>
              <span className="mx-2">/</span>
              <span className="text-orange-600 font-medium">Contas a Pagar</span>
            </nav>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upload Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload do Arquivo CSV
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragOver
                    ? 'border-orange-500 bg-orange-50'
                    : 'border-gray-300 hover:border-orange-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {isProcessing ? 'Processando arquivo...' : 'Arraste e solte seu arquivo CSV aqui'}
                </p>
                <p className="text-gray-500 mb-4">ou</p>
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Selecionar Arquivo
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Results */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
          >
            {/* Summary Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Resumo Financeiro
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">Total de Créditos</p>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(summary.totalCredito)}
                    </p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-600 font-medium">Total de Débitos</p>
                    <p className="text-2xl font-bold text-red-700">
                      {formatCurrency(summary.totalDebito)}
                    </p>
                  </div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Saldo Final</p>
                  <p className="text-3xl font-bold text-blue-700">
                    {formatCurrency(summary.saldoFinal)}
                  </p>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 font-medium">Total de Transações</p>
                  <p className="text-2xl font-bold text-gray-700">
                    {summary.totalTransacoes}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Ações
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={gerarCSVFormatado}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  size="lg"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Gerar CSV Formatado
                </Button>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    onClick={downloadJSON}
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    JSON
                  </Button>
                  <Button
                    onClick={copyJSON}
                    variant="outline"
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar
                  </Button>
                </div>
                
                {/* Informações sobre o processamento */}
                <div className="space-y-3 pt-4 border-t">
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                    <span className="text-purple-700 font-medium">Transferência Recebida da Conta Receita:</span>
                    <span className="text-purple-800 font-bold">
                      {(() => {
                        const transacoesCredito = csvData.filter(row => row.valor > 0);
                        const codigo1816 = transacoesCredito.filter(transacao => {
                          // Mesma lógica da função gerarCSVFormatado
                          const isEstornoOuDevolucao = transacao.descricao.startsWith('ESTORNO') || transacao.descricao.startsWith('Devolução');
                          // Código 18,16: NÃO é estorno E debitado é ZENDRY S.A
                          return !isEstornoOuDevolucao && transacao.debitado === 'ZENDRY S.A';
                        });
                        const totalCodigo1816 = codigo1816.reduce((sum, row) => sum + row.valor, 0);
                        return formatCurrency(totalCodigo1816);
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                    <span className="text-orange-700 font-medium">Crédito Referente a Estorno:</span>
                    <span className="text-orange-800 font-bold">
                      {(() => {
                        const transacoesCredito = csvData.filter(row => row.valor > 0);
                        const estornos = transacoesCredito.filter(transacao => {
                          const isEstornoOuDevolucao = transacao.descricao.startsWith('ESTORNO') || transacao.descricao.startsWith('Devolução');
                          return isEstornoOuDevolucao;
                        });
                        const totalEstornos = estornos.reduce((sum, row) => sum + row.valor, 0);
                        return formatCurrency(totalEstornos);
                      })()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                    <span className="text-green-700 font-medium">Crédito Referente a Outras Receitas:</span>
                    <span className="text-green-800 font-bold">
                      {(() => {
                        const transacoesCredito = csvData.filter(row => row.valor > 0);
                        const codigo182893 = transacoesCredito.filter(transacao => {
                          // Mesma lógica da função gerarCSVFormatado
                          const isEstornoOuDevolucao = transacao.descricao.startsWith('ESTORNO') || transacao.descricao.startsWith('Devolução');
                          // Código 18,2893: NÃO é estorno E debitado NÃO é ZENDRY S.A
                          return !isEstornoOuDevolucao && transacao.debitado !== 'ZENDRY S.A';
                        });
                        const totalCodigo182893 = codigo182893.reduce((sum, row) => sum + row.valor, 0);
                        return formatCurrency(totalCodigo182893);
                      })()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Auditoria OFX x CSV Contábil */}
            <Card className="lg:col-span-2 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <FileText className="w-5 h-5" />
                  Auditoria OFX x Conta Contábil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload OFX (extrato bancário)</label>
                    <div
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400"
                      onClick={() => ofxInputRef.current?.click()}
                    >
                      <p className="text-sm text-gray-600">Clique para selecionar o OFX</p>
                      {ofxData && (
                        <p className="text-xs text-blue-700 mt-2">
                          {ofxData.transacoes.length} transações carregadas
                        </p>
                      )}
                    </div>
                    <input
                      ref={ofxInputRef}
                      type="file"
                      accept=".ofx"
                      onChange={(e) => handleOFXUpload(e.target.files[0])}
                      className="hidden"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV contábil</label>
                    <div
                      className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400"
                      onClick={() => contabilInputRef.current?.click()}
                    >
                      <p className="text-sm text-gray-600">Clique para selecionar o CSV contábil</p>
                      {contabilData && (
                        <p className="text-xs text-blue-700 mt-2">
                          {contabilData.transacoes.length} lançamentos carregados
                        </p>
                      )}
                    </div>
                    <input
                      ref={contabilInputRef}
                      type="file"
                      accept=".csv"
                      onChange={(e) => handleContabilUpload(e.target.files[0])}
                      className="hidden"
                    />
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={realizarConciliacao}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                    disabled={!ofxData || !contabilData}
                  >
                    <Download className="w-4 h-4" />
                    Conciliar agora
                  </Button>
                  <span className="text-sm text-gray-500 self-center">
                    {(!ofxData || !contabilData) ? 'Carregue os dois arquivos para conciliar.' : 'Pronto para conciliar.'}
                  </span>
                </div>

                {conciliacaoResultado && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-blue-700">Total OFX</p>
                          <p className="text-lg font-bold text-blue-800">
                            {formatCurrency(conciliacaoResultado.totalOFX)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-green-50 border-green-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-green-700">Total Contábil</p>
                          <p className="text-lg font-bold text-green-800">
                            {formatCurrency(conciliacaoResultado.totalContabil)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-red-50 border-red-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-red-700">Diferença Total</p>
                          <p className="text-lg font-bold text-red-800">
                            {formatCurrency(conciliacaoResultado.diferencaTotal)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="bg-purple-50 border-purple-200">
                        <CardContent className="p-4">
                          <p className="text-xs text-purple-700">Taxa de Conciliação</p>
                          <p className="text-lg font-bold text-purple-800">
                            {conciliacaoResultado.taxaConciliacao.toFixed(1)}%
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm text-blue-700">Somente no OFX</CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => copiarDivergencias('ofx')}
                              disabled={!conciliacaoResultado.somenteOFX.length}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copiar
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                          {conciliacaoResultado.somenteOFX.map((item, idx) => (
                            <div key={`ofx-${idx}`} className="p-2 bg-blue-50 rounded-lg text-xs">
                              <p className="font-semibold">{item.data} • {formatCurrency(item.valorAbs)}</p>
                              <p className="text-gray-600">{item.memo}</p>
                            </div>
                          ))}
                          {!conciliacaoResultado.somenteOFX.length && (
                            <p className="text-xs text-gray-500">Nenhuma divergência encontrada no OFX.</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm text-orange-700">Somente no Contábil</CardTitle>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => copiarDivergencias('contabil')}
                              disabled={!conciliacaoResultado.somenteContabil.length}
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copiar
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
                          {conciliacaoResultado.somenteContabil.map((item, idx) => (
                            <div key={`contab-${idx}`} className="p-2 bg-orange-50 rounded-lg text-xs">
                              <p className="font-semibold">{item.data} • {formatCurrency(item.valorAbs)}</p>
                              <p className="text-gray-600">{item.historico}</p>
                            </div>
                          ))}
                          {!conciliacaoResultado.somenteContabil.length && (
                            <p className="text-xs text-gray-500">Nenhuma divergência encontrada no contábil.</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
